import { useContext, useEffect, useRef, useState } from 'react'
import { Flex, Form, Modal, Typography, Upload } from 'antd'
import {
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { postRequest } from '../../../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../../../context/global_context'
import { DefPluginContext, SHOW_DEF_BULK_UPLOAD } from '../context/context'
import InputText from '../../../common/InputText'
import Button from '../../../common/Button'
import { useIntl } from 'react-intl'

const DefBulkUpload = (props) => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [, dispatch] = useContext(NavContext)
  const [defPluginState, defPluginDispatch] = useContext(DefPluginContext)
  const [form] = Form.useForm()
  const [errors, setErrors] = useState({})
  const [uploaded, setUploaded] = useState(false)
  const listRef = useRef(null)

  const scrollRowIntoView = (rowEl) => {
    const listEl = listRef.current
    if (!listEl || !rowEl) return
    const listRect = listEl.getBoundingClientRect()
    const rowRect = rowEl.getBoundingClientRect()
    listEl.scrollTo({
      top: listEl.scrollTop + (rowRect.top - listRect.top) - 16,
      behavior: 'smooth',
    })
  }

  const onFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return
    const first = [...errorFields]
      .filter((field) => Array.isArray(field.name) && typeof field.name[1] === 'number')
      .sort((a, b) => a.name[1] - b.name[1])[0]
    if (!first) return
    scrollRowIntoView(
      listRef.current?.querySelector(`[data-row-index="${first.name[1]}"]`)
    )
  }

  useEffect(() => {
    if (Object.keys(errors).length === 0) return
    const firstRowIndex = Object.keys(errors)
      .filter((key) => errors[key])
      .sort((a, b) => Number(a) - Number(b))[0]
    if (firstRowIndex === undefined) return
    scrollRowIntoView(
      listRef.current?.querySelector(`[data-row-index="${firstRowIndex}"]`)
    )
  }, [errors])

  const submitBulkDefFiles = async (values) => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'defPlugin.uploadingCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    formData.append('total_files', values.files.length)

    values.files.forEach((item, index) => {
      if (item?.def_file?.[0]) {
        formData.append(
          `${index}-def_file`,
          item.def_file[0].originFileObj || item.def_file[0]
        )
      }
      if (item?.supp_file?.[0]) {
        formData.append(
          `${index}-supp_file`,
          item.supp_file[0].originFileObj || item.supp_file[0]
        )
      }
      formData.append(`${index}-file_id`, item?.file_id || '')
      formData.append(`${index}-abbreviation`, item?.abbreviation || '')
      formData.append(`${index}-description`, item?.description || '')
      formData.append('harness-id', item?.file_id || '')
    })

    let res = await postRequest(
      `/api/plugin/def/bulk_upload/${vehicle}/`,
      formData,
      false,
      {
        onUploadProgress: (progressEvent) => {
          const totalLength = progressEvent.lengthComputable
            ? progressEvent.total
            : progressEvent.target.getResponseHeader('content-length') ||
              progressEvent.target.getResponseHeader(
                'x-decompressed-content-length'
              )
          const percentageCompleted = Math.round(
            (progressEvent.loaded * 100) / totalLength
          )
          if (percentageCompleted === 100) {
            dispatch({ type: 'loader', data: {}, notification: {} })
          }
        },
      }
    )
    dispatch({ type: 'loader', data: {}, notification: {} })

    setErrors(res?.error || {})
    if (res?.error && Object.keys(res?.error)?.length > 0) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'defPlugin.bulkUploadError' }),
        },
      })
    } else {
      setUploaded(true)
    }
  }

  const closeModal = () => {
    defPluginDispatch({ type: SHOW_DEF_BULK_UPLOAD, data: false })
    props.onClose()
  }

  return (
    <Modal
      title={intl.formatMessage({ id: 'defPlugin.title' })}
      open={defPluginState?.show_bulk_upload}
      centered={true}
      onCancel={closeModal}
      width="90%"
      footer={null}
      className="def-plugin-bulk-upload"
      destroyOnHidden
    >
      {uploaded ? (
        <Flex
          align="center"
          justify="center"
          vertical
          gap={12}
          style={{ padding: 40 }}
        >
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
          <Typography.Text strong style={{ fontSize: 20 }}>
            {intl.formatMessage({ id: 'defPlugin.bulkUploadSuccessTitle' })}
          </Typography.Text>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'defPlugin.bulkUploadSuccessNote' })}
          </Typography.Text>
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={submitBulkDefFiles}
          onFinishFailed={onFinishFailed}
          initialValues={{ files: [{}] }}
        >
          <div className="upload-grid">
            <div className="grid-header-item">{intl.formatMessage({ id: 'defPlugin.defFile' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'defPlugin.suppFile' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'defPlugin.fileId' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'defPlugin.abbreviation' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'defPlugin.description' })}</div>
            <div></div>
          </div>

          <div className='bulk-upload-list' ref={listRef}>
          <Form.List name="files">
            {(fields, { add, remove }) => (
              <>
              <div className='list-container'>
                {fields.map(({ key, name }) => (
                  <div key={key} data-row-index={name} className="upload-grid">
                    <div className="grid-item-file">
                      <Form.Item
                        name={[name, 'def_file']}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList || []}
                        rules={[{ required: true }]}
                        help={false}
                        style={{ marginBottom: 0 }}
                      >
                        <Upload
                          beforeUpload={() => false}
                          maxCount={1}
                          accept=".def"
                        >
                          <Button icon={<UploadOutlined />}>
                            {intl.formatMessage({
                              id: 'defPlugin.chooseFile',
                            })}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </div>
                    <div className="grid-item-file">
                      <Form.Item
                        name={[name, 'supp_file']}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList || []}
                        style={{ marginBottom: 0 }}
                      >
                        <Upload beforeUpload={() => false} maxCount={1}>
                          <Button icon={<UploadOutlined />}>
                            {intl.formatMessage({
                              id: 'defPlugin.chooseFile',
                            })}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </div>
                    <div className="grid-item-input">
                      <InputText
                        name={[name, 'file_id']}
                        placeholder={intl.formatMessage({
                          id: 'defPlugin.fileId',
                        })}
                        isRequired={true}
                        hideLabel
                      />
                    </div>
                    <div className="grid-item-input">
                      <InputText
                        name={[name, 'abbreviation']}
                        placeholder={intl.formatMessage({
                          id: 'defPlugin.abbreviation',
                        })}
                        isRequired={false}
                        maxLength={6}
                        hideLabel
                      />
                    </div>
                    <div className="grid-item-input">
                      <InputText
                        name={[name, 'description']}
                        placeholder={intl.formatMessage({
                          id: 'defPlugin.description',
                        })}
                        isRequired={true}
                        hideLabel
                      />
                    </div>
                    <div className="grid-item-delete">
                      <Button
                        type="link"
                        className="link-button danger"
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1}
                        onClick={() => remove(name)}
                      />
                    </div>
                    {errors[name] && (
                      <div className="grid-item-error">
                        <Typography.Text type="danger">
                          {errors[name]}
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid-footer">
                <Flex justify="space-between">
                  <Button
                    type="link"
                    onClick={() => {
                      const nextIndex = fields.length
                      add()
                      requestAnimationFrame(() => {
                        scrollRowIntoView(
                          listRef.current?.querySelector(
                            `[data-row-index="${nextIndex}"]`
                          )
                        )
                      })
                    }}
                    className="link-button"
                    icon={<PlusOutlined />}
                  >
                    {intl.formatMessage({ id: 'defPlugin.addAnotherFile' })}
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {intl.formatMessage({
                      id: 'defPlugin.saveBulkDefFiles',
                    })}
                  </Button>
                </Flex>
              </div>
              </>
            )}
          </Form.List>
          </div>
        </Form>
      )}
    </Modal>
  )
}

export default DefBulkUpload
